import type { TChipTone, TChipVariant } from '@umodeapporg/ui/components/UI/Chips/chipTones';
import type { SymbolCodepoints } from '@umodeapporg/ui/components/UI/MaterialSymbol';

import type { FilaMotivo, FilaMotivoAjuste, FilaTipo } from '@/domain/fila/interfaces/fila';

/** How one reason is painted wherever a reason is named. */
export interface IMotivoVisual {
  tone: TChipTone;
  /** `soft` unless two reasons share a tone: the outline tells them apart. */
  variant?: TChipVariant;
  icon: SymbolCodepoints;
}

/**
 * ONE look and ONE icon per reason, so a row with two chips still reads at a glance.
 *
 * Only the library tones are used, each reason on the tone closest to the colour the
 * product owner approved for it (the categorical scale it used to borrow has no chip
 * tone). Every reason is something to act on, so none is on `success`. The tones are
 * shared by two reasons each, so the second one of a pair is drawn `outlined`: coral and
 * amber (`NAO_CABE`, `MATERIA_PRIMA`), pink and red (`FOLGA_CURTA`,
 * `SEM_CAUSA_CAPACIDADE`), blue and teal (`EQUIPE_REDUZIDA`, `DESVIO`), magenta and violet
 * (`QTD_A_CONFERIR`, `MAQUINA_PARADA`), indigo for the two that wait on someone else's
 * answer (`SEM_SEQUENCIA`, `NAO_VEIO_NO_VESTO`) and the two cell reasons.
 *
 * Mirrors `FilaTipoEnum` on the server, like the `FilaTipo` list in
 * `domain/fila/interfaces/fila.ts` and the filters of the screen: a new reason on the
 * server enters all of them.
 */
export const MOTIVO_VISUAL: Record<FilaTipo, IMotivoVisual> = {
  /** Legacy, kept so an old decision in the history still has a colour and an icon. */
  ENTREGA_VENCIDA: { tone: 'danger', icon: 'event_busy' },
  /**
   * The danger red, like the overdue chip it replaced: it is the SAME fact (the deadline
   * is gone and nothing else explains it), and the two never share a row, because one
   * only shows up on decisions recorded before the change.
   */
  SEM_CAUSA_CAPACIDADE: { tone: 'danger', icon: 'pending_actions' },
  NAO_CABE: { tone: 'warning', variant: 'outlined', icon: 'hourglass_bottom' },
  MATERIA_PRIMA: { tone: 'warning', icon: 'inventory_2' },
  FOLGA_CURTA: { tone: 'danger', variant: 'outlined', icon: 'running_with_errors' },
  DESVIO: { tone: 'info', icon: 'trending_down' },
  EQUIPE_REDUZIDA: { tone: 'info', variant: 'outlined', icon: 'group_remove' },
  MAQUINA_PARADA: { tone: 'purple', icon: 'build' },
  SEM_SEQUENCIA: { tone: 'indigo', icon: 'format_list_numbered' },
  CELULA_MANUAL_INATIVA: { tone: 'neutral', icon: 'block' },
  CELULA_PROIBIDA: { tone: 'neutral', variant: 'outlined', icon: 'rule' },
  QTD_A_CONFERIR: { tone: 'purple', variant: 'outlined', icon: 'pin' },
  QTD_CORRIGIDA_DIVERGE: { tone: 'warning', variant: 'outlined', icon: 'difference' },
  NAO_VEIO_NO_VESTO: { tone: 'indigo', variant: 'outlined', icon: 'help' },
  /**
   * Neutral and outlined: it is the only reason that does not report a problem of an OP,
   * it reports an opportunity of the whole portfolio. A warning tone would make it
   * compete with the rows that are actually stuck.
   */
  PROGRAMACAO_MELHOR: { tone: 'neutral', variant: 'outlined', icon: 'auto_awesome' },
};

/**
 * From the gravest to the mildest: a delay that already happened, one the calculation
 * predicts, a machine that is out of play, a material block, the pace, the day's
 * attendance that cut the capacity, and then what only describes the OP. The row shows
 * the first ones, so this order decides what the eye gets.
 *
 * A stopped machine sits next to the material block because it is the same kind of fact,
 * something physical the OP cannot run without, and above it because a machine is a
 * decision of the factory's own (fix it, move the work) while material waits on a third
 * party.
 */
const MOTIVOS_ORDEM: ReadonlyArray<FilaTipo> = [
  // Legacy first, so an old decision keeps reading the way it was recorded.
  'ENTREGA_VENCIDA',
  'NAO_CABE',
  'MAQUINA_PARADA',
  'MATERIA_PRIMA',
  'DESVIO',
  'EQUIPE_REDUZIDA',
  'CELULA_MANUAL_INATIVA',
  // Junto da celula desativada: as duas dizem que a OP esta numa celula que nao
  // deveria receber, e nenhuma das duas trava a producao de hoje.
  'CELULA_PROIBIDA',
  'FOLGA_CURTA',
  'SEM_SEQUENCIA',
  // Nao trava a OP: ela avisa que o numero pode estar dobrado e pede uma conferida.
  'QTD_A_CONFERIR',
  // Ao lado dela: a correcao a mao e o VESTO se afastaram e alguem precisa decidir.
  'QTD_CORRIGIDA_DIVERGE',
  // Na mesma faixa: a OP continua produzindo, o que falta e alguem dizer o que
  // aconteceu com ela no VESTO.
  'NAO_VEIO_NO_VESTO',
  // Last because it is the leftover: it only exists when no other cause matched, so it
  // never competes for the leading chip.
  'SEM_CAUSA_CAPACIDADE',
  // Depois de tudo: nada esta travado, e olhar a proposta cabe a qualquer hora.
  'PROGRAMACAO_MELHOR',
];

const gravidade = (tipo: FilaTipo): number => {
  const posicao = MOTIVOS_ORDEM.indexOf(tipo);
  return posicao === -1 ? MOTIVOS_ORDEM.length : posicao;
};

/**
 * The reasons of one OP in severity order, one chip per TYPE. The API may repeat a type
 * (two pending materials are two reasons), and two identical chips side by side would
 * spend the row's width saying the same thing twice.
 */
/**
 * The name of ONE reason on screen: the one the owner typed in Settings, or `padrao`
 * (the product's own) when she left it blank.
 *
 * EVERY place that names a reason goes through here: naming it in two places is how a
 * renamed reason ends up reading one way on the column and another on the filter for
 * the same thing. The default comes from the CALLER because the two places word it
 * differently on purpose ("Matéria-prima" on the chip, "Material a resolver" on the
 * filter), and a rename has to beat both without flattening them into one.
 */
export function rotuloDoMotivo(
  ajustes: ReadonlyArray<FilaMotivoAjuste> | undefined,
  tipo: FilaTipo,
  padrao: string,
): string {
  const rotulo = ajustes?.find((ajuste) => ajuste.tipo === tipo)?.rotulo ?? '';
  return rotulo.trim() === '' ? padrao : rotulo;
}

export function ordenarMotivos(motivos: Array<FilaMotivo> | undefined): Array<FilaMotivo> {
  const porTipo = new Map<FilaTipo, FilaMotivo>();
  for (const motivo of motivos ?? []) {
    if (!porTipo.has(motivo.tipo)) {
      porTipo.set(motivo.tipo, motivo);
    }
  }
  return [...porTipo.values()].sort((a, b) => gravidade(a.tipo) - gravidade(b.tipo));
}
